import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

interface WebhookLog {
  id: string;
  createdAt: string;
  ticker?: string;
  signal?: string;
  status: string;
  errorMessage?: string;
  response?: any;
  request?: any;
  secretHash?: string;
}

interface ApiError {
  timestamp: string;
  endpoint: string;
  method: string;
  status: number;
  statusText: string;
  error: any;
  request: any;
  response: any;
}

interface Trade {
  id: string;
  createdAt: string;
  ticker: string;
  action: string;
  units: string;
  status: string;
  executedPrice?: string;
  rejectionMessage?: string;
}

export function DebugPage() {
  const { user } = useAuth();
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [apiErrors, setApiErrors] = useState<ApiError[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testWebhookResult, setTestWebhookResult] = useState<any>(null);
  const [selectedLog, setSelectedLog] = useState<WebhookLog | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';

  // Fetch webhook logs
  const fetchWebhookLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/debug/webhook-logs`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch webhook logs');
      const data = await response.json();
      const logs = data.logs || [];
      setWebhookLogs(logs);
      setSelectedLog(prevSelected => {
        if (!logs.length) return null;
        if (prevSelected) {
          const existing = logs.find(log => log.id === prevSelected.id);
          if (existing) return existing;
        }
        return logs[0];
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch recent trades
  const fetchTrades = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/trading/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch trades');
      const data = await response.json();
      setTrades(data.trades || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Intercept and log API errors
  const captureApiError = (endpoint: string, method: string, status: number, statusText: string, error: any, request: any, response: any) => {
    const apiError: ApiError = {
      timestamp: new Date().toISOString(),
      endpoint,
      method,
      status,
      statusText,
      error,
      request,
      response
    };
    setApiErrors(prev => [apiError, ...prev].slice(0, 20)); // Keep last 20 errors
  };

  // Enhanced fetch with error capture
  const debugFetch = async (url: string, options?: RequestInit) => {
    const startTime = Date.now();
    try {
      const response = await fetch(url, options);
      const responseTime = Date.now() - startTime;

      // Clone response to read body
      const responseClone = response.clone();
      let responseData = null;
      try {
        responseData = await responseClone.json();
      } catch {
        responseData = await responseClone.text();
      }

      // Log if error status
      if (!response.ok) {
        captureApiError(
          url.replace(apiUrl, ''),
          options?.method || 'GET',
          response.status,
          response.statusText,
          responseData,
          {
            headers: options?.headers,
            body: options?.body ? JSON.parse(options.body as string) : null
          },
          {
            headers: Object.fromEntries(response.headers.entries()),
            responseTime: `${responseTime}ms`
          }
        );
      }

      return response;
    } catch (error) {
      captureApiError(
        url.replace(apiUrl, ''),
        options?.method || 'GET',
        0,
        'Network Error',
        error instanceof Error ? error.message : error,
        {
          headers: options?.headers,
          body: options?.body ? JSON.parse(options.body as string) : null
        },
        null
      );
      throw error;
    }
  };

  // Test webhook endpoint with detailed error capture
  const testWebhook = async () => {
    setLoading(true);
    setError(null);
    setTestWebhookResult(null);

    const testPayload = {
      secret: "test-secret-" + Date.now(),
      signal: "BUY",
      ticker: "TEST",
      units: 1
    };

    try {
      const response = await debugFetch(`${apiUrl}/api/webhooks/tradingview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'CF-Connecting-IP': '76.170.225.236', // Simulate allowed IP
        },
        body: JSON.stringify(testPayload),
      });

      const data = await response.json();
      setTestWebhookResult({
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        data,
        payload: testPayload,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Clear console logs
  const clearConsoleLogs = () => {
    console.clear();
    alert('Console logs cleared');
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const stringifyForDisplay = (value: unknown) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          const parsed = JSON.parse(value);
          return JSON.stringify(parsed, null, 2);
        } catch {
          return value;
        }
      }
      return value;
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };

  return (
    <div>

      {/* Quick Actions */}
      <motion.div 
        className="rh-card p-6 rh-hover-shadow rh-animate-fade-in mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Quick Actions</h2>
        <div className="flex gap-4">
          <motion.button
            onClick={fetchWebhookLogs}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Fetch Webhook Logs
          </motion.button>
          <motion.button
            onClick={fetchTrades}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Fetch Recent Trades
          </motion.button>
          <motion.button
            onClick={testWebhook}
            disabled={loading}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Test Webhook
          </motion.button>
          <motion.button
            onClick={clearConsoleLogs}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Clear Console
          </motion.button>
        </div>
      </motion.div>

      {/* Error Display */}
      {error && (
        <motion.div 
          className="rh-card p-6 rh-hover-shadow rh-animate-fade-in mb-8"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="text-red-800 font-semibold">Error</h3>
          <p className="text-red-600">{error}</p>
        </motion.div>
      )}

        {/* API Errors Log */}
        {apiErrors.length > 0 && (
          <div className="rh-card p-6 rh-hover-shadow rh-animate-fade-in mb-8" style={{ animationDelay: '0.2s' }}>
            <h3 className="text-red-800 font-semibold mb-4">Recent API Errors ({apiErrors.length})</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {apiErrors.map((apiError, index) => (
                <details key={index} className="bg-white p-3 rounded border border-red-200">
                  <summary className="cursor-pointer text-sm">
                    <span className="font-semibold">{apiError.method} {apiError.endpoint}</span>
                    <span className="ml-2 text-red-600">{apiError.status} {apiError.statusText}</span>
                    <span className="ml-2 text-gray-500">{apiError.timestamp}</span>
                  </summary>
                  <div className="mt-3 space-y-2 text-xs">
                    <div>
                      <strong>Request:</strong>
                      <pre className="mt-1 p-2 bg-gray-100 rounded overflow-auto">
                        {JSON.stringify(apiError.request, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <strong>Error Response:</strong>
                      <pre className="mt-1 p-2 bg-red-100 rounded overflow-auto">
                        {JSON.stringify(apiError.error, null, 2)}
                      </pre>
                    </div>
                    {apiError.response && (
                      <div>
                        <strong>Response Headers:</strong>
                        <pre className="mt-1 p-2 bg-gray-100 rounded overflow-auto">
                          {JSON.stringify(apiError.response, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              ))}
            </div>
            <button
              onClick={() => setApiErrors([])}
              className="mt-3 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
            >
              Clear Error Log
            </button>
          </div>
        )}

        {/* Test Webhook Result */}
        {testWebhookResult && (
          <div className="rh-card p-6 rh-hover-shadow rh-animate-fade-in mb-8" style={{ animationDelay: '0.3s' }}>
            <h3 className="text-yellow-800 font-semibold mb-2">Test Webhook Result</h3>
            <div className="space-y-2 text-sm">
              <p><strong>Status:</strong> {testWebhookResult.status} {testWebhookResult.statusText}</p>
              <p><strong>Timestamp:</strong> {testWebhookResult.timestamp}</p>
              <details>
                <summary className="cursor-pointer text-yellow-700">Payload Sent</summary>
                <pre className="mt-2 p-2 bg-white rounded text-xs overflow-auto">
                  {JSON.stringify(testWebhookResult.payload, null, 2)}
                </pre>
              </details>
              <details>
                <summary className="cursor-pointer text-yellow-700">Response</summary>
                <pre className="mt-2 p-2 bg-white rounded text-xs overflow-auto">
                  {JSON.stringify(testWebhookResult.data, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        )}

        {/* Rejected Webhook Signals (Blocked Stocks) */}
        {webhookLogs.filter(log => log.status === 'ignored' && log.errorMessage?.includes('blocked')).length > 0 && (
          <div className="rh-card p-6 rh-hover-shadow rh-animate-fade-in mb-8" style={{ animationDelay: '0.35s' }}>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              Rejected Webhook Signals (Blocked Stocks)
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              These webhook signals were rejected because the stock is blocked for webhook trading. 
              Visit the Webhook Stocks page to manage blocked stocks.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ticker</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Signal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Reason</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {webhookLogs
                    .filter(log => log.status === 'ignored' && log.errorMessage?.includes('blocked'))
                    .map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {formatDate(log.createdAt)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className="font-semibold text-gray-900 dark:text-white">{log.ticker || '-'}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                            log.signal === 'BUY' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 
                            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {log.signal || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {log.errorMessage}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Webhook Logs */}
        <div className="rh-card p-6 rh-hover-shadow rh-animate-fade-in mb-8" style={{ animationDelay: '0.4s' }}>
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Webhook Logs</h2>
            {webhookLogs.length === 0 ? (
              <p className="text-gray-500">No webhook logs. Click "Fetch Webhook Logs" to load.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticker</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signal</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {webhookLogs.map((log) => (
                        <tr
                          key={log.id}
                          onClick={() => setSelectedLog(log)}
                          className={`cursor-pointer hover:bg-blue-50 ${selectedLog?.id === log.id ? 'bg-blue-50' : ''}`}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatDate(log.createdAt)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{log.ticker || '-'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{log.signal || '-'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span
                              className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                                log.status === 'processed' ? 'bg-green-100 text-green-800' :
                                log.status === 'error' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {log.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {log.errorMessage ? `${log.errorMessage.substring(0, 60)}${log.errorMessage.length > 60 ? '...' : ''}` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {selectedLog && (
                  <div className="mt-6 space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Log Details</h3>
                      <button
                        onClick={() => setSelectedLog(null)}
                        className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        Clear Selection
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div><strong>ID:</strong> {selectedLog.id}</div>
                      <div><strong>Status:</strong> {selectedLog.status}</div>
                      <div><strong>Created At:</strong> {formatDate(selectedLog.createdAt)}</div>
                      <div><strong>Secret Hash:</strong> {selectedLog.secretHash || '-'}</div>
                      <div><strong>Ticker:</strong> {selectedLog.ticker || '-'}</div>
                      <div><strong>Signal:</strong> {selectedLog.signal || '-'}</div>
                    </div>
                    <div>
                      <strong>Request Payload:</strong>
                      <pre className="mt-1 p-2 bg-white rounded text-xs overflow-auto">
                        {stringifyForDisplay(selectedLog.request)}
                      </pre>
                    </div>
                    <div>
                      <strong>Response / Debug Info:</strong>
                      <pre className="mt-1 p-2 bg-white rounded text-xs overflow-auto">
                        {stringifyForDisplay(selectedLog.response)}
                      </pre>
                    </div>
                    {selectedLog.errorMessage && (
                      <div>
                        <strong>Error Message:</strong>
                        <div className="mt-1 p-2 bg-red-50 text-red-800 rounded">
                          {selectedLog.errorMessage}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
        </div>

        {/* Recent Trades */}
        <div className="rh-card p-6 rh-hover-shadow rh-animate-fade-in mb-8" style={{ animationDelay: '0.5s' }}>
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Recent Trades</h2>
            {trades.length === 0 ? (
              <p className="text-gray-500">No trades. Click "Fetch Recent Trades" to load.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticker</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Units</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {trades.map((trade) => (
                      <tr key={trade.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{formatDate(trade.createdAt)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{trade.ticker}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{trade.action}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{trade.units}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">${trade.executedPrice || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{trade.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>

        {/* Console Output */}
        <div className="rh-card p-6 rh-hover-shadow rh-animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <h3 className="text-white mb-2 font-semibold">Console Output (Check Browser Console)</h3>
          <p className="text-gray-400">Open DevTools (F12) → Console to see debug logs</p>
        </div>
    </div>
  );
}